import { ArrowRight, FileSpreadsheet } from "lucide-react";
import type { ReactNode } from "react";

import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { PayoutBatch } from "../../types/payoutBatches.types";
import { BatchStatusPill } from "./BatchStatusPill";
import { CorridorLabel } from "./CorridorLabel";

export interface BatchHeaderProps {
  batch: PayoutBatch;
  /** Right-aligned actions — the secondary menu, the history button. */
  actions?: ReactNode;
}

/** The batch's identity: file, status, corridor, currencies, size and when it was created. */
export function BatchHeader({ batch, actions }: BatchHeaderProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");

  return (
    <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileSpreadsheet aria-hidden="true" className="size-5" />
          </span>
          <h1 className="truncate text-xl font-semibold tracking-tight text-foreground">{batch.file_name}</h1>
          <BatchStatusPill status={batch.status} />
        </div>
        <dl className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <dt className="sr-only">{t("detail_corridor")}</dt>
            <dd>
              <CorridorLabel country={batch.country} rail={batch.rail} />
            </dd>
          </div>
          <div className="flex items-center gap-1.5 font-mono text-xs tabular-nums">
            <span>{batch.source_currency}</span>
            <ArrowRight aria-hidden="true" className="size-3" />
            <span>{batch.currency}</span>
          </div>
          <div>
            <dt className="sr-only">{t("detail_file")}</dt>
            <dd className="font-mono text-xs tabular-nums">{t("detail_rows", { count: batch.row_count })}</dd>
          </div>
          <div>
            <dt className="sr-only">{t("detail_created_at")}</dt>
            <dd className="font-mono text-xs tabular-nums">
              {formatDateTime(batch.created_at, {
                locale: activeLanguage,
                includeTime: true,
              })}
            </dd>
          </div>
          {batch.concept && <dd className="truncate">{batch.concept}</dd>}
        </dl>
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}
